import React, { useState, useEffect } from 'react';
import './LandingPage.css';
import testboxLogo from '../assets/testbox-logo-white.svg';

const LandingPage = ({ onStart }) => {
  const [file, setFile] = useState(null);
  const [productName, setProductName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [errors, setErrors] = useState({});
  const [visibleSections, setVisibleSections] = useState(['video']);

  // Progressive form disclosure
  useEffect(() => {
    setVisibleSections(prev => {
      const newSections = new Set(prev);
      if (file) newSections.add('product');
      if (productName.trim() && companyName.trim()) newSections.add('buyer');
      return Array.from(newSections);
    });
  }, [file, productName, companyName]);

  const validate = () => {
    const newErrors = {};
    if (!file) newErrors.file = 'Please select a video file';
    if (!productName.trim()) newErrors.productName = 'Product name is required';
    if (!companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!targetAudience.trim()) newErrors.targetAudience = 'Target audience is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validExtensions = ['.mov', '.mp4', '.avi'];
      const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
      if (validExtensions.includes(fileExtension)) {
        setFile(selectedFile);
        setErrors(prev => ({ ...prev, file: null }));
      } else {
        setFile(null);
        setErrors(prev => ({ ...prev, file: 'Invalid file type. Use .mov, .mp4, or .avi' }));
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const formData = { file, productName, companyName, targetAudience };
      onStart(formData);
    }
  };

  return (
    <div className="landing-page">
      <header className="landing-header">
        <img src={testboxLogo} alt="TestBox" className="landing-logo" />
      </header>

      <main className="landing-content">
        <div className="hero-section">
          <h1 className="hero-title">
            <span className="title-line">Demo Gap</span>
            <span className="title-line">Analysis</span>
          </h1>

          <p className="hero-subtitle">
            Most demos lose buyers in the first 3 minutes. Upload yours and get an executive-ready analysis showing exactly where buyers disengage and how to fix it.
          </p>

          <form onSubmit={handleSubmit} className="embedded-form">
            <div className="form-group">
              <label htmlFor="video-file">Video File *</label>
              <div className={`file-input-wrapper ${errors.file ? 'error' : ''}`}>
                <input
                  type="file"
                  id="video-file"
                  accept=".mov,.mp4,.avi"
                  onChange={handleFileChange}
                  className="file-input"
                />
                <div className="file-input-button">
                  {file ? (
                    <span className="file-selected">✓ {file.name}</span>
                  ) : (
                    <span>Choose Video File (.mov, .mp4, .avi)</span>
                  )}
                </div>
              </div>
              {errors.file && <span className="error-message">{errors.file}</span>}
            </div>

            {visibleSections.includes('product') && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="product-name">Product Name *</label>
                    <input
                      type="text"
                      id="product-name"
                      value={productName}
                      onChange={(e) => {
                        setProductName(e.target.value);
                        if (errors.productName) setErrors(prev => ({ ...prev, productName: null }));
                      }}
                      className={`text-input ${errors.productName ? 'error' : ''}`}
                      placeholder="e.g., Mailchimp"
                    />
                    {errors.productName && <span className="error-message">{errors.productName}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="company-name">Company Name *</label>
                    <input
                      type="text"
                      id="company-name"
                      value={companyName}
                      onChange={(e) => {
                        setCompanyName(e.target.value);
                        if (errors.companyName) setErrors(prev => ({ ...prev, companyName: null }));
                      }}
                      className={`text-input ${errors.companyName ? 'error' : ''}`}
                      placeholder="e.g., Shopify"
                    />
                    {errors.companyName && <span className="error-message">{errors.companyName}</span>}
                  </div>
                </div>
              </>
            )}

            {visibleSections.includes('buyer') && (
              <div className="form-group">
                <label htmlFor="target-audience">Target Buyer *</label>
                <textarea
                  id="target-audience"
                  value={targetAudience}
                  onChange={(e) => {
                    setTargetAudience(e.target.value);
                    if (errors.targetAudience) setErrors(prev => ({ ...prev, targetAudience: null }));
                  }}
                  className={`textarea-input ${errors.targetAudience ? 'error' : ''}`}
                  rows="3"
                  placeholder="e.g., VP of Sales, Marketing Directors"
                />
                {errors.targetAudience && <span className="error-message">{errors.targetAudience}</span>}
              </div>
            )}

            {visibleSections.includes('buyer') && targetAudience.trim() && (
              <button type="submit" className="cta-button">
                START ANALYSIS
              </button>
            )}
          </form>
        </div>
      </main>

      <footer className="landing-footer">
        <p>© 2026 TestBox Inc. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default LandingPage;
